<template>
  <div>
    <head>
      <meta charset="utf-8" />
      <title>HOSPITAL MANAGEMENT INFORMATION SYSTEM | System Management</title>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <meta content="Manage system configuration and administrative data" name="description" />
      <meta content="Kelvin Njuguna Ndungu" name="author" />
      <!-- App favicon -->
      <link rel="shortcut icon" href="assets/images/favicon.svg">

      <!-- DataTables -->
      <link href="assets/libs/datatables.net-bs4/css/dataTables.bootstrap4.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/libs/datatables.net-buttons-bs4/css/buttons.bootstrap4.min.css" rel="stylesheet" type="text/css" />

      <!-- Bootstrap Css -->
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <!-- Bootstrap select styling -->
      <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-select@1.13.14/dist/css/bootstrap-select.min.css">
      <!-- Icons Css -->
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <!-- App Css-->
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css"/>
      <!-- custom Css-->
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css"/>
    </head>

    <body data-sidebar="dark">
    <!-- Begin page -->
    <div id="layout-wrapper">
      <!-- ========== Left Sidebar Start ========== -->
      <NavigationBar/>
      <!-- Left Sidebar End -->

      <!-- ============================================================== -->
      <!-- Start right Content here -->
      <!-- ============================================================== -->
      <div class="main-content">
        <div class="page-content">
          <div class="container-fluid">
            <!-- start page title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">System Management</h4>

                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="index.html">Dashboards</a></li>
                      <li class="breadcrumb-item active">System Management</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
            <!-- end page title -->

            <!-- Management Tabs -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header">
                    <ul class="nav nav-tabs card-header-tabs" role="tablist">
                      <li class="nav-item" v-for="tab in tabs" :key="tab.id">
                        <a
                            class="nav-link"
                            :class="{ active: activeTab === tab.id }"
                            @click="switchTab(tab.id)"
                            href="javascript: void(0);"
                        >
                          <i :class="tab.icon"></i>
                          {{ tab.label }}
                        </a>
                      </li>
                    </ul>
                  </div>

                  <div class="card-body">
                    <!-- Alert Messages -->
                    <div v-if="alert.success" class="alert alert-success alert-dismissible fade show" role="alert">
                      <i class="mdi mdi-check-all me-2"></i>
                      {{ message }}
                      <button type="button" class="btn-close" @click="clearAlerts" aria-label="Close"></button>
                    </div>

                    <div v-if="alert.info" class="alert alert-info alert-dismissible fade show" role="alert">
                      <i class="mdi mdi-information-outline me-2"></i>
                      {{ message }}
                      <button type="button" class="btn-close" @click="clearAlerts" aria-label="Close"></button>
                    </div>

                    <div v-if="alert.failed" class="alert alert-danger alert-dismissible fade show" role="alert">
                      <i class="mdi mdi-block-helper me-2"></i>
                      {{ message }}
                      <button type="button" class="btn-close" @click="clearAlerts" aria-label="Close"></button>
                    </div>

                    <!-- Search and Actions Bar -->
                    <div class="row mb-3">
                      <div class="col-md-6">
                        <div class="input-group">
                          <input
                              type="text"
                              class="form-control"
                              v-model="searchTerm"
                              @input="performSearch"
                              :placeholder="`Search ${getCurrentTabLabel()}...`"
                          >
                          <button class="btn btn-outline-secondary" type="button">
                            <i class="mdi mdi-magnify"></i>
                          </button>
                        </div>
                      </div>
                      <div class="col-md-6 text-end">
                        <button
                            class="btn btn-primary"
                            @click="openCreateModal"
                            :disabled="loading"
                        >
                          <i class="mdi mdi-plus me-2"></i>
                          Add {{ getCurrentTabLabel().slice(0, -1) }}
                        </button>
                        <button
                            class="btn btn-outline-secondary ms-2"
                            @click="refreshData"
                            :disabled="loading"
                        >
                          <i class="mdi mdi-refresh me-2"></i>
                          Refresh
                        </button>
                      </div>
                    </div>

                    <!-- Data Table -->
                    <div class="table-responsive">
                      <table class="table table-striped table-hover">
                        <thead class="table-dark">
                        <tr>
                          <th>ID</th>
                          <th v-for="column in getCurrentColumns()" :key="column.key">
                            {{ column.label }}
                          </th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-if="loading">
                          <td :colspan="getCurrentColumns().length + 3" class="text-center py-4">
                            <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                            Loading...
                          </td>
                        </tr>
                        <tr v-else-if="filteredData.length === 0">
                          <td :colspan="getCurrentColumns().length + 3" class="text-center py-4 text-muted">
                            No {{ getCurrentTabLabel().toLowerCase() }} found
                          </td>
                        </tr>
                        <tr v-else v-for="item in filteredData" :key="getItemId(item)">
                          <td>{{ getItemId(item) }}</td>
                          <td v-for="column in getCurrentColumns()" :key="column.key">
                              <span v-if="column.type === 'badge'" class="badge" :class="getBadgeClass(item[column.key])">
                                {{ item[column.key] }}
                              </span>
                            <span v-else>{{ item[column.key] || '-' }}</span>
                          </td>
                          <td>
                              <span class="badge" :class="item.status === 'active' ? 'bg-success' : 'bg-secondary'">
                                {{ item.status || 'active' }}
                              </span>
                          </td>
                          <td>
                            <div class="btn-group btn-group-sm">
                              <button
                                  class="btn btn-outline-primary"
                                  @click="viewItem(item)"
                                  title="View Details"
                              >
                                <i class="mdi mdi-eye"></i>
                              </button>
                              <button
                                  class="btn btn-outline-secondary"
                                  @click="editItem(item)"
                                  title="Edit"
                              >
                                <i class="mdi mdi-pencil"></i>
                              </button>
                              <button
                                  class="btn btn-outline-danger"
                                  @click="confirmDelete(item)"
                                  title="Delete"
                                  v-if="canDelete(item)"
                              >
                                <i class="mdi mdi-delete"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Pagination -->
                    <div class="row mt-3" v-if="!loading && filteredData.length > 0">
                      <div class="col-md-6">
                        <p class="text-muted mb-0">
                          Showing {{ filteredData.length }} of {{ totalRecords }} entries
                        </p>
                      </div>
                      <div class="col-md-6">
                        <nav aria-label="Page navigation">
                          <ul class="pagination pagination-sm justify-content-end mb-0">
                            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                              <a class="page-link" @click="changePage(currentPage - 1)" href="javascript: void(0);">Previous</a>
                            </li>
                            <li
                                class="page-item"
                                v-for="page in totalPages"
                                :key="page"
                                :class="{ active: currentPage === page }"
                            >
                              <a class="page-link" @click="changePage(page)" href="javascript: void(0);">{{ page }}</a>
                            </li>
                            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                              <a class="page-link" @click="changePage(currentPage + 1)" href="javascript: void(0);">Next</a>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Modal for Create/Edit/View -->
            <div v-if="showModal" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
              <div class="modal-dialog modal-lg modal-dialog-centered">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title">
                      <i :class="getModalIcon()"></i>
                      {{ getModalTitle() }}
                    </h5>
                    <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
                  </div>
                  <div class="modal-body">
                    <!-- Debug section - Remove in production -->


                    <form @submit.prevent="submitForm" v-if="modalMode !== 'view'">
                      <div class="row">
                        <!-- Dynamic form fields based on active tab -->
                        <div
                            v-for="field in getCurrentFormFields()"
                            :key="field.key"
                            :class="field.colClass || 'col-md-6'"
                        >
                          <div class="mb-3">
                            <label :for="field.key" class="form-label">
                              {{ field.label }}
                              <span v-if="field.required" class="text-danger">*</span>
                            </label>

                            <!-- Text Input -->
                            <input
                                v-if="field.type === 'text' || field.type === 'email'"
                                :type="field.type"
                                class="form-control"
                                :id="field.key"
                                v-model="formData[field.key]"
                                :placeholder="field.placeholder"
                                :required="field.required"
                            >

                            <!-- Textarea -->
                            <textarea
                                v-else-if="field.type === 'textarea'"
                                class="form-control"
                                :id="field.key"
                                v-model="formData[field.key]"
                                :placeholder="field.placeholder"
                                :rows="field.rows || 3"
                            ></textarea>

                            <!-- Select -->
                            <select
                                v-else-if="field.type === 'select'"
                                class="form-select"
                                :id="field.key"
                                v-model="formData[field.key]"
                                :required="field.required"
                                @change="onFieldChange(field.key, $event.target.value)"
                                :key="field.key + ':' + (
        field.key === 'regionId' ? selectOptions.regions.length :
        field.key === 'zoneId'   ? selectOptions.zones.length   :
        field.key === 'woredaId' ? selectOptions.woredas.length  : ''
      )"
                            >


                            <option value="">{{ field.placeholder || `Select ${field.label}` }}</option>
                              <option
                                  v-for="option in getFieldOptions(field.key)"
                                  :key="option.value"
                                  :value="option.value"
                              >
                                {{ option.label }}
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </form>

                    <!-- View Mode -->
                    <div v-else class="row">
                      <div class="col-12">
                        <table class="table table-borderless">
                          <tbody>
                          <tr v-for="field in getCurrentFormFields()" :key="field.key">
                            <td class="fw-bold text-end pe-3" style="width: 30%;">{{ field.label }}:</td>
                            <td>{{ selectedItem[field.key] || '-' }}</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="closeModal">
                      <i class="mdi mdi-close me-2"></i>
                      {{ modalMode === 'view' ? 'Close' : 'Cancel' }}
                    </button>
                    <button
                        v-if="modalMode !== 'view'"
                        type="button"
                        class="btn btn-primary"
                        @click="submitForm"
                        :disabled="submitting"
                    >
                      <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                      <i v-else :class="modalMode === 'create' ? 'mdi mdi-plus' : 'mdi mdi-content-save'" class="me-2"></i>
                      {{ modalMode === 'create' ? 'Create' : 'Update' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Delete Confirmation Modal -->
            <div v-if="showDeleteModal" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
              <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                  <div class="modal-header bg-danger text-white">
                    <h5 class="modal-title">
                      <i class="mdi mdi-delete me-2"></i>
                      Confirm Deletion
                    </h5>
                    <button type="button" class="btn-close btn-close-white" @click="closeDeleteModal"></button>
                  </div>
                  <div class="modal-body">
                    <div class="text-center py-3">
                      <i class="mdi mdi-alert-circle text-warning" style="font-size: 4rem;"></i>
                      <h5 class="mt-3">Are you sure?</h5>
                      <p class="text-muted">
                        This action cannot be undone. This will permanently delete the
                        {{ getCurrentTabLabel().slice(0, -1).toLowerCase() }}.
                      </p>
                      <div class="alert alert-warning" v-if="itemToDelete">
                        <strong>{{ getItemDisplayName(itemToDelete) }}</strong>
                      </div>
                    </div>
                  </div>
                  <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="closeDeleteModal">
                      <i class="mdi mdi-close me-2"></i>
                      Cancel
                    </button>
                    <button
                        type="button"
                        class="btn btn-danger"
                        @click="deleteItem"
                        :disabled="deleting"
                    >
                      <span v-if="deleting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                      <i v-else class="mdi mdi-delete me-2"></i>
                      {{ deleting ? 'Deleting...' : 'Delete' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import { authUrl, executeJson } from "@/api";
import router from "@/router";

export default {
  name: "SystemManagement",
  components: { NavigationBar },
  data() {
    return {
      loading: false,
      submitting: false,
      deleting: false,
      loadingParentData: false,
      activeTab: 'employment-types',
      searchTerm: '',
      showModal: false,
      showDeleteModal: false,
      modalMode: 'create', // 'create', 'edit', 'view'
      selectedItem: null,
      itemToDelete: null,
      data: [],
      formData: {},

      // Dynamic options storage for select fields
      selectOptions: {
        regions: [],
        zones: [],
        woredas: [],
        statuses: [
          { value: 'active', label: 'Active' },
          { value: 'inactive', label: 'Inactive' }
        ],
        countryCodes: [
          { value: 'ET', label: 'Ethiopia (ET)' },
          { value: 'KE', label: 'Kenya (KE)' },
          { value: 'UG', label: 'Uganda (UG)' },
          { value: 'TZ', label: 'Tanzania (TZ)' },
          { value: 'RW', label: 'Rwanda (RW)' },
          { value: 'OTHER', label: 'Other' }
        ],
        organizationLevels: [
          { value: 1, label: 'National' },
          { value: 2, label: 'Regional' },
          { value: 3, label: 'Zonal' },
          { value: 4, label: 'Woreda' },
          { value: 5, label: 'Facility' }
        ],
        yesNo: [
          { value: '0', label: 'No' },
          { value: '1', label: 'Yes' }
        ]
      },

      currentPage: 1,
      totalRecords: 0,
      pageSize: 10,

      // Alert states
      alert: {
        success: false,
        info: false,
        failed: false
      },
      message: '',

      // Tab configuration
      tabs: [
        {
          id: 'employment-types',
          label: 'Employment Types',
          icon: 'mdi mdi-account-tie',
          api: 'getEmploymentTypes',
          createApi: 'createEmploymentType',
          updateApi: 'updateEmploymentType',
          deleteApi: 'deactivateEmploymentType',
          detailsApi: 'getEmploymentTypeDetails'
        },
        {
          id: 'affiliations',
          label: 'Affiliations',
          icon: 'mdi mdi-domain',
          api: 'getAffiliatedInstitutions',
          createApi: 'createAffiliatedInstitution',
          updateApi: 'updateAffiliatedInstitution',
          deleteApi: 'deactivateAffiliatedInstitution',
          detailsApi: 'getAffiliatedInstitutionDetails'
        },
        {
          id: 'roles',
          label: 'System Roles',
          icon: 'mdi mdi-account-key',
          api: 'getRoles',
          createApi: 'createRole',
          updateApi: 'updateRole',
          deleteApi: 'deactivateRole',
          detailsApi: 'getRoleDetails'
        },
        {
          id: 'departments',
          label: 'Departments',
          icon: 'mdi mdi-office-building',
          api: 'getDepartments',
          createApi: 'createDepartment',
          updateApi: 'updateDepartment',
          deleteApi: 'deactivateDepartment',
          detailsApi: 'getDepartmentDetails'
        },
        {
          id: 'regions',
          label: 'Regions',
          icon: 'mdi mdi-map',
          api: 'getRegions',
          createApi: 'createRegion',
          updateApi: 'updateRegion',
          deleteApi: 'deactivateRegion'
        },
        {
          id: 'zones',
          label: 'Zones',
          icon: 'mdi mdi-map-marker-radius',
          api: 'getZones',
          createApi: 'createZone',
          updateApi: 'updateZone',
          deleteApi: 'deactivateZone'
        },
        {
          id: 'woredas',
          label: 'Woredas',
          icon: 'mdi mdi-map-marker-circle',
          api: 'getWoredas',
          createApi: 'createWoreda',
          updateApi: 'updateWoreda',
          deleteApi: 'deactivateWoreda'
        },
        {
          id: 'kebeles',
          label: 'Kebeles',
          icon: 'mdi mdi-map-marker',
          api: 'getKebeles',
          createApi: 'createKebele',
          updateApi: 'updateKebele',
          deleteApi: 'deactivateKebele'
        }
      ]
    };
  },

  computed: {
    filteredData() {
      if (!this.searchTerm) return this.data;

      const term = this.searchTerm.toLowerCase();
      return this.data.filter(item => {
        return Object.values(item).some(value =>
            value && value.toString().toLowerCase().includes(term)
        );
      });
    },

    totalPages() {
      return Math.ceil(this.totalRecords / this.pageSize);
    }
  },

  mounted() {
    this.checkAuthentication();
    this.loadData();

  },

  methods: {
    checkAuthentication() {
      const isLoggedIn = sessionStorage.getItem("isLoggedIn");
      const authToken = localStorage.getItem("authToken");

      if (!isLoggedIn || !authToken) {
        router.push('/login');
      }
    },

    switchTab(tabId) {
      this.activeTab = tabId;
      this.searchTerm = '';
      this.currentPage = 1;
      this.loadData();
      this.clearAlerts();
    },

    getIdField() {
      const idFields = {
        'employment-types': 'employment_type_id',
        'affiliations': 'institution_id',
        'roles': 'role_id',
        'departments': 'department_id',
        'regions': 'region_id',
        'zones': 'zone_id',
        'woredas': 'woreda_id',
        'kebeles': 'kebele_id'
      };
      return idFields[this.activeTab] || 'id';
    },

    async loadData() {
      this.loading = true;
      const currentTab = this.getCurrentTab();

      try {
        const data = {
          function: currentTab.api,
          searchTerm: this.searchTerm,
          limit: this.pageSize,
          offset: (this.currentPage - 1) * this.pageSize
        };

        const response = await executeJson(data, authUrl);

        if (response.data.success) {
          const responseData = response.data.data || response.data;
          this.data = this.extractDataFromResponse(responseData);
          this.totalRecords = responseData.total || this.data.length;
        } else {
          this.showAlert('failed', response.data.message || 'Failed to load data');
        }
      } catch (error) {
        console.error("Error loading data:", error);
        this.showAlert('failed', 'Error loading data');
      } finally {
        this.loading = false;
      }
    },

    async loadRegions() {
      try {
        console.log('[loadRegions] calling getRegions…');
        const res = await executeJson({ function: 'getRegions' }, authUrl);
        console.log('[loadRegions] raw response:', res?.data);

        if (!res?.data?.success) {
          console.error('[loadRegions] unsuccessful:', res?.data?.message);
          this.selectOptions.regions = [];          // <-- plain assignment
          return false;
        }

        const raw = res?.data?.data?.regions || [];
        if (!Array.isArray(raw)) {
          console.error('[loadRegions] regions is not an array:', raw);
          this.selectOptions.regions = [];          // <-- plain assignment
          return false;
        }

        const normalized = raw.map(r => ({
          value: String(r.region_id ?? r.id ?? ''),
          label: String(r.region_name ?? r.name ?? ''),
        })).filter(o => o.value && o.label);

        this.selectOptions.regions = normalized;    // <-- plain assignment (reactive in Vue 3)
        console.log('[loadRegions] normalized options:', normalized);
        return normalized.length > 0;
      } catch (e) {
        console.error('[loadRegions] exception:', e);
        this.selectOptions.regions = [];            // <-- plain assignment
        return false;
      }
    },


    async loadZones(regionId = null) {
      try {
        const data = { function: 'getZones' };
        if (regionId) data.regionId = regionId;

        const response = await executeJson(data, authUrl);

        if (response?.data?.success) {
          let zones = [];
          if (response.data.data?.zones) zones = response.data.data.zones;
          else if (response.data.zones) zones = response.data.zones;
          else if (Array.isArray(response.data.data)) zones = response.data.data;

          this.selectOptions.zones = zones.map(z => ({
            value: String(z.zone_id ?? z.id ?? ''),
            label: String(z.zone_name ?? z.name ?? z.zoneName ?? ''),
          }));                                      // <-- plain assignment

          console.log('[loadZones] count:', this.selectOptions.zones.length);
        } else {
          this.selectOptions.zones = [];            // <-- plain assignment
        }
      } catch (e) {
        console.error('Error loading zones:', e);
        this.selectOptions.zones = [];              // <-- plain assignment
      }
    },


    async loadWoredas(zoneId = null) {
      try {
        const data = { function: 'getWoredas' };
        if (zoneId) data.zoneId = zoneId;

        const response = await executeJson(data, authUrl);

        if (response?.data?.success) {
          let woredas = [];
          if (response.data.data?.woredas) woredas = response.data.data.woredas;
          else if (response.data.woredas) woredas = response.data.woredas;
          else if (Array.isArray(response.data.data)) woredas = response.data.data;

          this.selectOptions.woredas = woredas.map(w => ({
            value: String(w.woreda_id ?? w.id ?? ''),
            label: String(w.woreda_name ?? w.name ?? w.woredaName ?? ''),
          }));                                      // <-- plain assignment

          console.log('[loadWoredas] count:', this.selectOptions.woredas.length);
        } else {
          this.selectOptions.woredas = [];          // <-- plain assignment
        }
      } catch (e) {
        console.error('Error loading woredas:', e);
        this.selectOptions.woredas = [];            // <-- plain assignment
      }
    },


    extractDataFromResponse(responseData) {
      // Extract data based on tab type
      switch (this.activeTab) {
        case 'employment-types':
          return responseData.employmentTypes || [];
        case 'affiliations':
          return responseData.institutions || [];
        case 'roles':
          return responseData.data?.roles || responseData.roles || [];
        case 'departments':
          return responseData.departments || [];
        case 'regions':
          return responseData.regions || [];
        case 'zones':
          return responseData.zones || [];
        case 'woredas':
          return responseData.woredas || [];
        case 'kebeles':
          return responseData.kebeles || [];
        default:
          return [];
      }
    },

    getCurrentTab() {
      return this.tabs.find(tab => tab.id === this.activeTab);
    },

    getCurrentTabLabel() {
      return this.getCurrentTab()?.label || '';
    },

    getCurrentColumns() {
      switch (this.activeTab) {
        case 'employment-types':
          return [
            { key: 'employment_type_name', label: 'Name' },
            { key: 'abbreviation', label: 'Abbreviation' },
            { key: 'description', label: 'Description' },
            { key: 'employee_count', label: 'Employees', type: 'badge' }
          ];
        case 'affiliations':
          return [
            { key: 'institution_name', label: 'Institution Name' },
            { key: 'institution_code', label: 'Code' },
            { key: 'country_code', label: 'Country' },
            { key: 'employee_count', label: 'Employees', type: 'badge' }
          ];
        case 'roles':
          return [
            { key: 'role_name', label: 'Role Name' },
            { key: 'role_code', label: 'Code' },
            { key: 'level_name', label: 'Organization Level' },
            { key: 'user_count', label: 'Users', type: 'badge' }
          ];
        case 'departments':
          return [
            { key: 'department_name', label: 'Department Name' },
            { key: 'abbreviation', label: 'Abbreviation' },
            { key: 'manager_name', label: 'Manager' },
            { key: 'employee_count', label: 'Employees', type: 'badge' }
          ];
        case 'regions':
          return [
            { key: 'region_name', label: 'Region Name' },
            { key: 'region_code', label: 'Region Code' }
          ];
        case 'zones':
          return [
            { key: 'zone_name', label: 'Zone Name' },
            { key: 'zone_code', label: 'Zone Code' },
            { key: 'region_name', label: 'Region' }
          ];
        case 'woredas':
          return [
            { key: 'woreda_name', label: 'Woreda Name' },
            { key: 'woreda_code', label: 'Woreda Code' },
            { key: 'zone_name', label: 'Zone' }
          ];
        case 'kebeles':
          return [
            { key: 'kebele_name', label: 'Kebele Name' },
            { key: 'kebele_code', label: 'Kebele Code' },
            { key: 'woreda_name', label: 'Woreda' }
          ];
        default:
          return [];
      }
    },

    getCurrentFormFields() {
      switch (this.activeTab) {
        case 'employment-types':
          return [
            { key: 'employmentTypeName', label: 'Employment Type Name', type: 'text', required: true, placeholder: 'Enter employment type name' },
            { key: 'abbreviation', label: 'Abbreviation', type: 'text', placeholder: 'Enter abbreviation' },
            { key: 'status', label: 'Status', type: 'select', required: true },
            { key: 'description', label: 'Description', type: 'textarea', colClass: 'col-12', placeholder: 'Enter description' }
          ];
        case 'affiliations':
          return [
            { key: 'institutionName', label: 'Institution Name', type: 'text', required: true, placeholder: 'Enter institution name' },
            { key: 'institutionCode', label: 'Institution Code', type: 'text', placeholder: 'Enter institution code' },
            { key: 'countryCode', label: 'Country Code', type: 'select', required: true },
            { key: 'status', label: 'Status', type: 'select', required: true },
            { key: 'description', label: 'Description', type: 'textarea', colClass: 'col-12', placeholder: 'Enter description' }
          ];
        case 'roles':
          return [
            { key: 'roleName', label: 'Role Name', type: 'text', required: true, placeholder: 'Enter role name' },
            { key: 'roleCode', label: 'Role Code', type: 'text', required: true, placeholder: 'Enter role code' },
            { key: 'organizationLevelId', label: 'Organization Level', type: 'select', required: true },
            { key: 'description', label: 'Description', type: 'textarea', colClass: 'col-12', placeholder: 'Enter description' }
          ];
        case 'departments':
          return [
            { key: 'departmentName', label: 'Department Name', type: 'text', required: true, placeholder: 'Enter department name' },
            { key: 'abbreviation', label: 'Abbreviation', type: 'text', placeholder: 'Enter abbreviation' },
            { key: 'managerName', label: 'Manager Name', type: 'text', placeholder: 'Enter manager name' },
            { key: 'isClinic', label: 'Is Clinic', type: 'select' },
            { key: 'status', label: 'Status', type: 'select', required: true },
            { key: 'description', label: 'Description', type: 'textarea', colClass: 'col-12', placeholder: 'Enter description' }
          ];
        case 'regions':
          return [
            { key: 'regionName', label: 'Region Name', type: 'text', required: true, placeholder: 'Enter region name' },
            { key: 'regionCode', label: 'Region Code', type: 'text', required: true, placeholder: 'Enter region code' }
          ];
        case 'zones':
          return [
            { key: 'zoneName', label: 'Zone Name', type: 'text', required: true, placeholder: 'Enter zone name' },
            { key: 'zoneCode', label: 'Zone Code', type: 'text', required: true, placeholder: 'Enter zone code' },
            { key: 'regionId', label: 'Region', type: 'select', required: true }
          ];
        case 'woredas':
          return [
            { key: 'woredaName', label: 'Woreda Name', type: 'text', required: true, placeholder: 'Enter woreda name' },
            { key: 'woredaCode', label: 'Woreda Code', type: 'text', required: true, placeholder: 'Enter woreda code' },
            { key: 'zoneId', label: 'Zone', type: 'select', required: true }
          ];
        case 'kebeles':
          return [
            { key: 'kebeleName', label: 'Kebele Name', type: 'text', required: true, placeholder: 'Enter kebele name' },
            { key: 'kebeleCode', label: 'Kebele Code', type: 'text', placeholder: 'Enter kebele code' },
            { key: 'woredaId', label: 'Woreda', type: 'select', required: true }
          ];
        default:
          return [];
      }
    },

    // New method to get options for a specific field
    getFieldOptions(fieldKey) {
      console.log(`Getting options for field: ${fieldKey}`);

      // Map field keys to their corresponding options
      const optionsMap = {
        'status': this.selectOptions.statuses,
        'countryCode': this.selectOptions.countryCodes,
        'organizationLevelId': this.selectOptions.organizationLevels,
        'isClinic': this.selectOptions.yesNo,
        'regionId': this.selectOptions.regions,
        'zoneId': this.selectOptions.zones,
        'woredaId': this.selectOptions.woredas
      };

      const options = optionsMap[fieldKey] || [];
      console.log(`Options for ${fieldKey}:`, options);

      return options;
    },

    getBadgeClass(value) {
      if (typeof value === 'string' && !isNaN(value)) {
        const numValue = parseInt(value);
        return numValue > 0 ? 'bg-primary' : 'bg-secondary';
      }
      if (typeof value === 'number') {
        return value > 0 ? 'bg-primary' : 'bg-secondary';
      }
      return 'bg-info';
    },

    canDelete(item) {
      // Prevent deletion of items with dependencies
      const countFields = ['employee_count', 'user_count'];
      return !countFields.some(field => item[field] && parseInt(item[field]) > 0);
    },

    getItemDisplayName(item) {
      const nameFields = [
        'employment_type_name', 'institution_name', 'role_name',
        'department_name', 'region_name', 'zone_name', 'woreda_name', 'kebele_name'
      ];

      for (const field of nameFields) {
        if (item[field]) return item[field];
      }

      return 'Item';
    },

    async openCreateModal() {
      // reset modal state
      this.modalMode = 'create';
      this.selectedItem = null;
      this.formData = {};
      this.loadingParentData = true;
      this.clearAlerts();

      try {
        switch (this.activeTab) {
          case 'zones': {
            const ok = await this.loadRegions(); // <-- must return true
            if (!ok) this.showAlert('info', 'No regions found. Please add regions first.');
            break;
          }
          case 'woredas': {
            await Promise.all([ this.loadRegions(), this.loadZones() ]);
            break;
          }
          case 'kebeles': {
            await Promise.all([ this.loadRegions(), this.loadZones(), this.loadWoredas() ]);
            break;
          }
          default:
            // nothing to preload
            break;
        }

        // force a DOM tick so the select sees the new options
        await this.$nextTick();

        // show the modal after options exist (still show even if empty so the alert is visible)
        this.showModal = true;

        // extra visibility in console
        console.log('[openCreateModal] regions length:', this.selectOptions.regions?.length || 0);
      } catch (e) {
        console.error('[openCreateModal] error:', e);
        this.showAlert('failed', 'Error loading required data.');
        this.showModal = true; // still open so user sees the message
      } finally {
        this.loadingParentData = false;
      }
    },



    async editItem(item) {
      this.modalMode = 'edit';
      this.selectedItem = item;

      // Load parent data if needed
      if (this.activeTab === 'zones') {
        await this.loadRegions();
      } else if (this.activeTab === 'woredas') {
        await this.loadRegions();
        await this.loadZones(item.region_id);
      } else if (this.activeTab === 'kebeles') {
        await this.loadRegions();
        await this.loadZones();
        await this.loadWoredas(item.zone_id);
      }

      // Wait for Vue to update the DOM
      await this.$nextTick();

      // Map database field names to form field names
      this.formData = this.mapItemToFormData(item);
      this.showModal = true;
    },

    mapItemToFormData(item) {
      const formData = { ...item };

      // Map employment types fields
      if (this.activeTab === 'employment-types') {
        formData.employmentTypeName = item.employment_type_name;
      }

      // Map affiliations fields
      if (this.activeTab === 'affiliations') {
        formData.institutionName = item.institution_name;
        formData.institutionCode = item.institution_code;
        formData.countryCode = item.country_code;
      }

      // Map roles fields
      if (this.activeTab === 'roles') {
        formData.roleName = item.role_name;
        formData.roleCode = item.role_code;
        formData.organizationLevelId = item.organization_level_id;
      }

      // Map departments fields
      if (this.activeTab === 'departments') {
        formData.departmentName = item.department_name;
        formData.managerName = item.manager_name;
        formData.isClinic = item.is_clinic;
      }

      // Map regions fields
      if (this.activeTab === 'regions') {
        formData.regionName = item.region_name;
        formData.regionCode = item.region_code;
      }

      // Map zones fields
      if (this.activeTab === 'zones') {
        formData.zoneName = item.zone_name;
        formData.zoneCode = item.zone_code;
        formData.regionId = String(item.region_id);
      }

      // Map woredas fields
      if (this.activeTab === 'woredas') {
        formData.woredaName = item.woreda_name;
        formData.woredaCode = item.woreda_code;
        formData.zoneId = String(item.zone_id);
      }

      // Map kebeles fields
      if (this.activeTab === 'kebeles') {
        formData.kebeleName = item.kebele_name;
        formData.kebeleCode = item.kebele_code;
        formData.woredaId = String(item.woreda_id);
      }

      return formData;
    },

    viewItem(item) {
      this.modalMode = 'view';
      this.selectedItem = item;
      this.formData = {};
      this.showModal = true;
    },

    confirmDelete(item) {
      this.itemToDelete = item;
      this.showDeleteModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.selectedItem = null;
      this.formData = {};
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.itemToDelete = null;
    },

    getModalTitle() {
      const label = this.getCurrentTabLabel().slice(0, -1); // Remove 's' from plural
      switch (this.modalMode) {
        case 'create': return `Add New ${label}`;
        case 'edit': return `Edit ${label}`;
        case 'view': return `View ${label} Details`;
        default: return label;
      }
    },

    getModalIcon() {
      switch (this.modalMode) {
        case 'create': return 'mdi mdi-plus';
        case 'edit': return 'mdi mdi-pencil';
        case 'view': return 'mdi mdi-eye';
        default: return 'mdi mdi-information';
      }
    },

    async submitForm() {
      if (!this.validateForm()) return;

      this.submitting = true;
      const currentTab = this.getCurrentTab();

      try {
        const apiFunction = this.modalMode === 'create' ? currentTab.createApi : currentTab.updateApi;
        const data = {
          function: apiFunction,
          ...this.formData
        };

        // Add ID for updates with proper field mapping
        if (this.modalMode === 'edit' && this.selectedItem) {
          const idField = this.getIdField();
          const apiIdField = this.getApiIdField();
          data[apiIdField] = this.getItemId(this.selectedItem);
        }

        const response = await executeJson(data, authUrl);

        if (response.data.success) {
          this.showAlert('success', response.data.message || `${this.getCurrentTabLabel().slice(0, -1)} ${this.modalMode === 'create' ? 'created' : 'updated'} successfully`);
          this.closeModal();
          this.loadData();
        } else {
          this.showAlert('failed', response.data.message || 'Operation failed');
        }
      } catch (error) {
        console.error("Error submitting form:", error);
        this.showAlert('failed', 'An error occurred while processing your request');
      } finally {
        this.submitting = false;
      }
    },

    getApiIdField() {
      // API expected ID field names (camelCase)
      const apiIdFields = {
        'employment-types': 'employmentTypeId',
        'affiliations': 'institutionId',
        'roles': 'roleId',
        'departments': 'departmentId',
        'regions': 'regionId',
        'zones': 'zoneId',
        'woredas': 'woredaId',
        'kebeles': 'kebeleId'
      };
      return apiIdFields[this.activeTab] || 'id';
    },

    async deleteItem() {
      if (!this.itemToDelete) return;

      this.deleting = true;
      const currentTab = this.getCurrentTab();

      try {
        const data = {
          function: currentTab.deleteApi,
          [this.getApiIdField()]: this.getItemId(this.itemToDelete),
          reason: 'Deleted from system management'
        };

        const response = await executeJson(data, authUrl);

        if (response.data.success) {
          this.showAlert('success', response.data.message || `${this.getCurrentTabLabel().slice(0, -1)} deleted successfully`);
          this.closeDeleteModal();
          this.loadData();
        } else {
          this.showAlert('failed', response.data.message || 'Deletion failed');
        }
      } catch (error) {
        console.error("Error deleting item:", error);
        this.showAlert('failed', 'An error occurred while deleting');
      } finally {
        this.deleting = false;
      }
    },

    getItemId(item) {
      const idFields = {
        'employment-types': 'employment_type_id',
        'affiliations': 'institution_id',
        'roles': 'role_id',
        'departments': 'department_id',
        'regions': 'region_id',
        'zones': 'zone_id',
        'woredas': 'woreda_id',
        'kebeles': 'kebele_id'
      };
      return item[idFields[this.activeTab]] || item.id || 'N/A';
    },

    validateForm() {
      const fields = this.getCurrentFormFields();
      const requiredFields = fields.filter(field => field.required);

      for (const field of requiredFields) {
        if (!this.formData[field.key] || this.formData[field.key].toString().trim() === '') {
          this.showAlert('failed', `${field.label} is required`);
          return false;
        }
      }

      return true;
    },

    async onFieldChange(fieldKey, value) {
      // Handle cascading dropdowns
      if (fieldKey === 'regionId') {
        this.formData.zoneId = '';
        this.formData.woredaId = '';
        if (value) {
          await this.loadZones(value);
        } else {
          this.selectOptions.zones = [];
        }
        this.selectOptions.woredas = [];
      } else if (fieldKey === 'zoneId') {
        this.formData.woredaId = '';
        if (value) {
          await this.loadWoredas(value);
        } else {
          this.selectOptions.woredas = [];
        }
      }
    },

    performSearch() {
      // Debounce search
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadData();
      }, 500);
    },

    refreshData() {
      this.searchTerm = '';
      this.currentPage = 1;
      this.loadData();
    },

    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.loadData();
      }
    },

    clearAlerts() {
      this.alert.success = false;
      this.alert.info = false;
      this.alert.failed = false;
      this.message = '';
    },

    showAlert(type, message) {
      this.clearAlerts();
      this.alert[type] = true;
      this.message = message;

      // Auto-hide success/info alerts after 5 seconds
      if (type === 'success' || type === 'info') {
        setTimeout(() => {
          this.clearAlerts();
        }, 5000);
      }
    }
  }
};
</script>

<style scoped>
.nav-tabs .nav-link {
  color: #74788d;
  border: none;
  border-bottom: 2px solid transparent;
  font-weight: 500;
}

.nav-tabs .nav-link.active {
  color: #556ee6;
  border-bottom-color: #556ee6;
  background-color: transparent;
}

.nav-tabs .nav-link:hover {
  border-bottom-color: #556ee6;
  background-color: transparent;
}

.table th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  color: #495057;
}

.btn-group-sm .btn {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.badge {
  font-size: 0.75rem;
  padding: 0.35em 0.65em;
}

.modal.show {
  animation: fadeIn 0.3s ease-in-out;
}

.alert {
  border-radius: 0.375rem;
  border: none;
  box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.075);
  animation: slideDown 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.table-responsive {
  max-height: 600px;
  overflow-y: auto;
}

.pagination .page-link {
  color: #556ee6;
  border-color: #dee2e6;
}

.pagination .page-item.active .page-link {
  background-color: #556ee6;
  border-color: #556ee6;
}

.pagination .page-link:hover {
  color: #556ee6;
  background-color: #e9ecef;
  border-color: #dee2e6;
}
</style>